import { ApiError } from "./api";

/**
 * Appends a stable, greppable error code plus whatever real diagnostic
 * detail is available (HTTP status + the backend's own message, or just
 * the raw JS error when the request never reached the backend at all —
 * e.g. a network/CORS failure) to a human-readable fallback message.
 *
 * Exists because a generic "Could not load X." banner is useless for
 * diagnosing a prod-only failure the developer can't reproduce and has no
 * direct access to (see docs/PRODUCTION_ACCESS.md / the OVH VPS prod
 * setup) — the user can read the code+detail off their own screen and
 * report it back instead. `code` should be a short, stable per-call-site
 * tag (e.g. "ERR-PI-LOAD") so the same failure always reads identically;
 * never derive it from the error itself.
 *
 * Intentionally minimal — no central registry, no i18n, no error
 * taxonomy. Apply this call-by-call as more spots get instrumented,
 * rather than building that structure before a second use case exists.
 */
export function describeError(code: string, fallbackMessage: string, error: unknown): string {
  if (error instanceof ApiError) {
    return `${fallbackMessage} (Error ${code}: HTTP ${error.status} — ${error.message})`;
  }
  if (error instanceof Error) {
    return `${fallbackMessage} (Error ${code}: ${error.message})`;
  }
  return `${fallbackMessage} (Error ${code})`;
}
